/**
 * How often a reader says they are still here, and how long that is believed.
 *
 * Both live here rather than beside either side of the wire, because the client
 * asks on one of them and the server reclaims on the other: two copies of one
 * figure would drift and the symptom would be readers flickering out of a room
 * nobody had left. `purgeAt` keeps a deletion grace period in one place for the
 * same reason.
 */

/**
 * How often a reader refreshes their row while the book is open.
 *
 * It is the latency of the whole feature: somebody arriving is seen within one
 * of these, and it is what a reader means by "they are here now". Ten seconds
 * because the answer is a face appearing beside a book rather than a time
 * anybody reads off a clock — a second would spend a phone's battery on a
 * question nobody is waiting on, and a minute would make arriving feel like a
 * page reload.
 *
 * The ask is only made while the tab is VISIBLE, which is what separates it
 * from the timer `build.ts` refuses: that one asks a question nobody is waiting
 * on, on a phone in shul. This one runs on one page, while somebody is looking
 * at the answer, and stops when they are not.
 */
export const PRESENCE_POLL_MS = 10 * 1000

/**
 * How often a reader asks while they are FOLLOWING somebody.
 *
 * Ten seconds is fine for a face appearing beside a book and far too slow for a
 * page that is meant to keep up with somebody turning it — at that rate a
 * follower arrives a sentence behind and reads it as broken rather than as
 * late.
 *
 * The expensive mode is paid for by the person who chose it: nobody follows by
 * accident, nobody follows in a hidden tab, and it stops the moment they scroll
 * away. So the cost is bounded by intent rather than by how many books are
 * open.
 */
export const PRESENCE_FOLLOW_POLL_MS = 2 * 1000

/**
 * How old a row may get before the room stops counting it.
 *
 * Three polls. The two directions cost different things, and three is where
 * they balance: shorter and an ordinary hiccup — a phone changing masts, a
 * laptop pausing timers for a moment — drops a reader who never left, and a
 * roster that flickers is worse than one that lags. Longer and somebody who
 * closed the book lingers in the room after they have gone.
 *
 * It bounds the ghost by the POLL rather than by the scheduler: the read
 * ignores anything older than this, so a reader who closed a tab is already
 * invisible before the sweep runs. The sweep only reclaims the rows.
 */
export const PRESENCE_STALE_MS = 3 * PRESENCE_POLL_MS

/**
 * The most anonymous rows one book will count.
 *
 * A signed-in reader is bounded by their own identity — `personId` is bound
 * from the session, so inventing rows only ever inflates their own tab count. A
 * reader on a public link has no such binding and nothing stops a client
 * minting tab ids, so the bound has to be the table's.
 *
 * Past this the roster says "so many or more" rather than a number, which is
 * the honest reading of a count that has hit its ceiling. Set where a real
 * shiur will not reach it and a script will.
 */
export const PRESENCE_ANONYMOUS_CAP = 99
